"use client";

import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { LinkProps as NextLinkProps } from "next/link";
import Link from "next/link";
import React, { FC, useState } from "react";

import useMediaQuery from "@/hooks/useMediaQuery";

interface InternalLinkProps extends NextLinkProps {
  href: string;
  query?: string | null | undefined;
  className?: string | undefined;
  children?: React.ReactElement | React.ReactElement[] | string;
  span?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLElement> | undefined;
  onTouchStart?: React.TouchEventHandler<HTMLElement> | undefined;
  onClick?: React.MouseEventHandler<HTMLElement> | undefined;
  [key: string]: any;
  poneNumberCta?: string | boolean | Function | null | undefined;
  isIntentForm?: boolean;
}

const linkDisabled = "btn-disabled";

const AnchorLink: FC<InternalLinkProps> = ({ href, query, className, children, span, poneNumberCta, isIntentForm, ...rest }) => {
  const isExternal = /^(https?:\/\/|mailto:|tel:)/.test(href);
  const [isOpen, setIsOpen] = useState(false);
  const { disabled } = rest as InternalLinkProps;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const t = useTranslations();
  const overlayVariant = {
    open: { opacity: 1, display: "block" },
    close: { opacity: 0, transitionEnd: { display: "none" } },
  };
  const handleToggle = (event?: React.MouseEvent<HTMLElement>) => {
    setIsOpen(!isOpen);
    if (typeof poneNumberCta === "function") {
      poneNumberCta();
    }

    if (typeof rest.onClick === "function" && event) {
      rest.onClick(event); // Pass the event if needed
    }
  };
  const popupVariant = {
    open: { y: "0%", display: "block", transition: { ease: "easeOut", duration: 0.3 } },
    close: (breakpoint: boolean) => {
      return {
        y: breakpoint ? "30%" : "100%",
        transition: { ease: "easeIn", duration: 0.3 },
      };
    },
  };
  const renderDropDown = () => (
    <motion.div
      id="modal-callus"
      variants={overlayVariant}
      initial="close"
      animate={isOpen ? "open" : "close"}
      className={`h-dvh md:h-auto bg-[rgba(0,0,0,0.3)] md:bg-transparent z-50`}
    >
      <div className="w-auto h-auto md:h-auto flex items-end md:items-center md:container">
        <div className="w-auto md:w-fit">
          <div className="mx-auto header-modal w-auto md:w-fit md:min-w-[150px]">
            <motion.div
              initial={"close"}
              variants={popupVariant}
              animate={isOpen ? "open" : "close"}
              className="bg-white px-m py-s rounded-t-[30px] md:rounded-lg relative shadow-strong"
            >
              <div className="bg-gray-2 mx-auto w-[36px] h-[5px] rounded md:hidden"></div>
              {/* <div className="underline-offset-4 font-secondary text-s flex justify-between items-center text-black">{href}</div> */}
              <div className="underline-offset-4 font-secondary text-s flex justify-between items-center text-black">
                {" "}
                {href?.includes("tel:") ? (
                  <>
                    {/* {t("label.common.tel")}: */}
                    {href.split(":")[1]}
                  </>
                ) : (
                  href
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
  // Need to check for header there displaying the number
  if (isDesktop && href?.includes("tel:")) {
    return (
      <>
        <span
          className={[disabled ? linkDisabled : " ", "relative", className].join(" ")}
          onClick={(e) => handleToggle(e)}
          style={{ cursor: "pointer" }}
        >
          {children}
          {isOpen && (
            <div style={{ position: "absolute", [isIntentForm ? "bottom" : "top"]: "100%", left: "0", zIndex: 1000 }}>{renderDropDown()}</div>
          )}
        </span>
      </>
    );
  }

  return isExternal ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={[disabled ? linkDisabled : " ", className].join(" ")}
      {...rest}
      onClick={(e) => handleToggle(e)}
    >
      {children}
    </a>
  ) : span ? (
    <span className={[disabled ? linkDisabled : " ", className].join(" ")} {...rest}>
      {children}
    </span>
  ) : (
    <Link
      href={
        href === "#"
          ? "#"
          : {
              pathname: href || "",
              query: query,
            }
      }
      className={[disabled ? linkDisabled : " ", className].join(" ")}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default AnchorLink;
